"use client";

import { db } from "@utils/IndexedDb";
import { ANIMATION_START_Y } from "@utils/animation";
import { getCookie, setCookie } from "cookies-next";
import { OptionsType } from "cookies-next/lib/types";
import { useLiveQuery } from "dexie-react-hooks";
import { AnimatePresence, motion } from "framer-motion";
import { useLocale, useTranslations } from "next-intl";
import { useSearchParams } from "next/navigation";
import React, { ChangeEvent, ComponentPropsWithRef, Dispatch, forwardRef, useContext, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { ControllerFieldState, ControllerRenderProps } from "react-hook-form";

import { HEADER_DESKTOP } from "@/components/Header/HeaderDesktop";
import FilterAction from "@/components/ui/Form/Filters/FilterAction";
import { SEARCH_BAR_AS_PER_PAGE, SEARCH_PAGE } from "@/components/ui/Form/Filters/FilterUtils";
import { LOCATION_MOBILE } from "@/components/ui/Form/Filters/LocationMobile";
import {
  MAX_AUTOCOMPLETE_SELECTION,
  groupLocation,
  highlightInput,
  locationsFromID,
  sortByLabelSimilarity,
  valuesToString,
} from "@/components/ui/Form/Filters/components/Autocomplete/AutocompleteUtils";
import "@/components/ui/Form/Filters/components/Autocomplete/autocomplete.css";
import Label from "@/components/ui/Form/Label/Label";
import Icon from "@/components/ui/Icon/Icon";
import { locationlocale } from "@/constants/locale";
import { DEFAULT_TYPE, SearchContext } from "@/context/SearchContext";
import { UserContext } from "@/context/UserContext";
import { clearAllTrackingEvent, lastSearchClick, searchSuggestionClick, showAllUnitsClick } from "@/dataLayer/dataLayer";
import { useOutsideClick } from "@/hooks/useOutsideClick";
import { GroupedLabelValue, LocationDB, SearchParams } from "@/types";
import cn from "@/utils/cn";

const minimumCharactersSuggestions = 2;

const panelVariation = {
  open: { opacity: 1, y: 0, display: "grid" },
  close: { opacity: 0, y: ANIMATION_START_Y, display: "none", transitionEnd: { display: "none" } },
};

type ValueReducerAction = {
  action: "ADD" | "REMOVE" | "CLEAR" | "BULK_ADD";
  value: string | string[];
};

export interface AutocompleteBaseProps extends ComponentPropsWithRef<"input"> {
  name: string;
  reverse?: boolean;
  label?: string;
  field?: ControllerRenderProps;
  fieldState?: ControllerFieldState;
  containerClassNames?: string;
  icon?: string;
  fullHeight?: boolean;
  container?: string;
  isHomePage?: boolean;
  isAutoGeneratedCarousel?: boolean;
}

const AutocompleteBase = forwardRef<HTMLInputElement, AutocompleteBaseProps>(
  (
    {
      name,
      reverse,
      label,
      field,
      containerClassNames,
      icon,
      fullHeight = false,
      container = "",
      isHomePage = true,
      isAutoGeneratedCarousel = false,
      ...rest
    },
    ref,
  ) => {
    const valueReducer = (currentState: string[], { action, value }: ValueReducerAction): string[] => {
      let finalValue: string[] = [];
      switch (action) {
        case "ADD":
          finalValue = currentState.length < 3 ? [...new Set([...currentState, value as string])] : currentState;
          break;
        case "REMOVE":
          finalValue = [...new Set([...currentState.filter((item) => item !== (value as string))])];
          break;
        case "CLEAR":
          finalValue = [];
          break;
        case "BULK_ADD":
          finalValue = value as string[];
          break;
        default:
          finalValue = currentState;
      }
      return finalValue;
    };

    const t = useTranslations();
    const { counterParams, counterTotal } = useContext(SearchContext);
    const { trendingSearch, recentSearch, setRecentSearch } = useContext(UserContext);
    const innerInput = useRef<HTMLInputElement>(null);
    const [typing, setTyping] = useState<boolean>(false);
    const [displaySuggestions, setDisplaySuggestions] = useState<boolean>(false);
    const [filteredResults, setFilteredResults] = useState<GroupedLabelValue>({});
    const [textValue, setTextValue] = useState<string>("");
    const [locationValues, setLocationValues] = useState<string[]>([]);
    const [hasLocations, setHasLocations] = useState<boolean>(false);
    const [recentItems, setRecentItems] = useState<LocationDB[]>([]);
    const [trendingItems, setTrendingItems] = useState<LocationDB[]>([]);
    const [value, setValue] = useReducer(valueReducer, field?.value ?? []);
    const [valueItems, setValueItems] = useState<LocationDB[]>([]);
    const [currentSearchText, setCurrentTextSearch] = useState<string>("");
    const [userCurrentSelection, setUserCurrentSelection] = useState<string>();
    const locations = useLiveQuery(() => db.LOCATIONS.toArray());
    const containerRef = useRef<HTMLDivElement>(null);
    const { type, params, isClearAll, setIsClearAll } = useContext(SearchContext);
    const locale = useLocale();
    const [language, region] = locale.split("-");
    const [locationsId, setLocationsId] = useState<string>("");
    const prevCounterTotal = useRef(counterTotal);
    function handleChange(event: ChangeEvent<HTMLInputElement>) {
      if (!!field) {
        let currentValues: string[] = Array.isArray(field.value) ? field.value : [field.value];
        setUserCurrentSelection(event.target.value.toString());
        if (event.target.checked) {
          setValue({
            action: "ADD",
            value: event.target.value,
          });
          currentValues.push(event.target.value);
          if (typeof recentSearch !== "undefined" && typeof setRecentSearch === "function") {
            // setRecentSearch(event.target.value);
          }
        } else {
          setValue({
            action: "REMOVE",
            value: event.target.value,
          });
          currentValues = currentValues.filter((item: string) => item !== event.target.value);
        }
        currentValues = currentValues.filter((item) => !!item);
        field.onChange(currentValues);
        setValue({ action: "BULK_ADD", value: currentValues });
      }
      if (rest.onChange) rest.onChange(event);
      // setTextValue("");
      // innerInput?.current?.focus();
    }

    const displayTrendingAndRecent = useMemo(() => {
      return !!!textValue || textValue.length < minimumCharactersSuggestions;
    }, [textValue]);

    useEffect(() => {
      console.log("Counter total changed, resetting autocomplete state", counterTotal, recentSearch);
      const CookieExpirationDate = new Date();
      CookieExpirationDate.setFullYear(CookieExpirationDate.getFullYear() + 10);
      const cookieOptions: OptionsType = { expires: CookieExpirationDate, sameSite: "lax" };
      if (prevCounterTotal.current !== counterTotal) {
        if (counterTotal !== 0 && userCurrentSelection) {
          setRecentSearch(userCurrentSelection);
          return;
        }
        const rcstring = getCookie("recentSearch");
        if (!!rcstring) {
          const recentSearchValues = rcstring.split(",");
          const otherSearchIds = recentSearchValues.filter((id) => id !== userCurrentSelection);
          setCookie("recentSearch", otherSearchIds.join(","), cookieOptions);
        }
        prevCounterTotal.current = counterTotal;
      }
    }, [counterTotal]);

    useEffect(() => {
      const checkLocations = async () => {
        const locationsCount = await db.LOCATIONS.count();
        setHasLocations(locationsCount > 0);
        if (counterParams?.category?.length && field) {
          // Ensure it's always an array
          const categories = Array.isArray(counterParams.category) ? counterParams.category : [counterParams.category];

          field.onChange(categories);
        }
      };
      checkLocations();
      if (innerInput.current) {
        innerInput.current.addEventListener("focus", () => setDisplaySuggestions(true));
        return () => {
          innerInput.current?.removeEventListener("focus", () => setDisplaySuggestions(true));
        };
      }

      return () => {
        // safer: clear via ref or just rely on remount state reset
        setLocationValues([]);
      };
    }, []);

    useEffect(() => {
      if (isClearAll) {
        setValue({
          action: "CLEAR",
          value: "",
        });
        setTextValue("");
        setLocationsId("");
        field?.onChange([]);
        setIsClearAll(false);
      }
    }, [isClearAll]);

    useEffect(() => {
      const sortResults = async () => {
        const regexp = new RegExp(currentSearchText, "i");
        const allItems = await db.LOCATIONS.toArray();
        const sortedItems = sortByLabelSimilarity(allItems, currentSearchText).map((item, index) => {
          return { ...item, order: index };
        });
        const isArabic = language === "ar";
        const isRussian = language === "ru";

        const bulkSort = sortedItems.map((item) => {
          // let active = regexp.test(item.name) && !valueItems.map((i) => i.id).includes(item.id);
          let active = false;

          if (isArabic || isRussian) {
            // Match against Arabic translations in locationlocale
            const translatedData = locationlocale.find((localeItem) => localeItem.id === item.id);
            if (translatedData) {
              active =
                ((isArabic &&
                  (translatedData.name?.ar?.includes(currentSearchText) ||
                    translatedData.location?.ar?.includes(currentSearchText) ||
                    translatedData.area?.ar?.includes(currentSearchText) ||
                    translatedData.city?.ar?.includes(currentSearchText))) ||
                  (isRussian &&
                    (translatedData.name?.ru?.includes(currentSearchText) ||
                      translatedData.location?.ru?.includes(currentSearchText) ||
                      translatedData.area?.ru?.includes(currentSearchText) ||
                      translatedData.city?.ru?.includes(currentSearchText)))) &&
                !valueItems.map((i) => i.id).includes(item.id);
            }
          } else {
            // Default matching logic for non-Arabic locales
            active = regexp.test(item.name) && !valueItems.map((i) => i.id).includes(item.id);
          }
          if (active) {
            active = (params.type === DEFAULT_TYPE && item.products > 1) || (!params.type && item.products > 1) || params.type === "project";
          }
          return {
            key: item.id,
            changes: { active: active, order: item.order },
          };
        });
        await db.LOCATIONS.bulkUpdate(bulkSort);
      };
      sortResults();
    }, [currentSearchText, valueItems, type]);

    useEffect(() => {
      if (!hasLocations || !!!recentSearch || !!!trendingSearch) return;
      const populateRecents = async () => {
        const rcstring = getCookie("recentSearch");
        if (!!rcstring) {
          const recentSearchValues = rcstring.split(",");
          const recentSearchIDS = recentSearchValues.reverse().splice(0, MAX_AUTOCOMPLETE_SELECTION);
          const rc = await db.LOCATIONS.where("id").anyOf(recentSearchIDS).toArray();
          setRecentItems(rc);
        }
      };
      const populateTrending = async () => {
        if (trendingSearch.length > 0 && trendingItems.length === 0) {
          const locationWithInventory = await db.PROJECTS.filter(
            (project) => trendingSearch.includes(project.assignedCategory) && project.inventoryAvailable === true,
          ).toArray();
          const trendingSearchWithInventory = locationWithInventory.map((project) => project.assignedCategory);
          const ts = await db.LOCATIONS.where("id").anyOf(trendingSearchWithInventory).toArray();
          setTrendingItems(ts);
        }
      };
      populateRecents();
      populateTrending();
    }, [hasLocations, recentSearch, trendingSearch, trendingItems]);

    // riattivo per sincronizzare i parametri tra querystring e autocomplete
    useEffect(() => {
      if (!locations) return;
      setFilteredResults(groupLocation(locations));
    }, [locations]);

    useEffect(() => {
      if (!hasLocations || !typing || textValue.length < minimumCharactersSuggestions) return;
      setCurrentTextSearch(valuesToString(textValue).at(-1) as string);
    }, [textValue, typing, hasLocations]);

    useOutsideClick(containerRef, () => {
      if (container === LOCATION_MOBILE) {
        return; // Do nothing for mobile view
      }

      setDisplaySuggestions(false);
    });

    useEffect(() => {
      if (!displaySuggestions) {
        document.body.classList.remove("overflow-hidden");
      } else {
        document.body.classList.add("overflow-hidden");
      }
    }, [displaySuggestions]);

    useEffect(() => {
      if (value.length > 0) {
        db.LOCATIONS.where("id")
          .anyOf(value)
          .toArray()
          .then((items) => setValueItems(items));
      } else {
        setValueItems([]);
      }
    }, [value]);
    useEffect(() => {
      if (!hasLocations || !!!field?.value) {
        setTextValue("");
        setLocationValues([]);
        return;
      }
      locationsFromID(field?.value).then((locations) => {
        setTextValue("");
        setLocationValues(locations);
      });
    }, [hasLocations, field?.value]);
    useEffect(() => {
      if ([HEADER_DESKTOP, LOCATION_MOBILE].includes(container)) {
        innerInput?.current?.focus();
        setDisplaySuggestions(true);
      }
    }, []);
    const getLocationTranslation = (id: string, lang: string | "") => {
      const translation = locationlocale.find((item) => item.id === id);

      if (translation) {
        const name = translation.name as Record<string, string>;
        const location = translation.location as Record<string, string>;
        const area = translation.area as Record<string, string>;
        const country = translation.country as Record<string, string>;
        const city = translation.city as Record<string, string>;

        return {
          name: name[lang] ?? name["en"],
          location: location[lang] ?? location["en"],
          area: area[lang] ?? area["en"],
          country: country[lang] ?? country["en"],
          city: city[lang] ?? city["en"],
        };
      }

      return null;
    };
    useEffect(() => {
      if (params?.category) {
        setLocationsId(params?.category?.[0]);
      }
    }, [params?.category]);

    const handleFilterSelection = async (params: SearchParams, searchTerm: string | number) => {
      let location = "All location";
      if (params.category) {
        location = (await locationsFromID(params.category as string[])).join(",");
      }
      showAllUnitsClick(
        location,
        Array.isArray(params.propertyType) ? params.propertyType.join(", ") : params.propertyType ?? "Any",
        Array.isArray(params.bedrooms) ? params.bedrooms.join(", ") : params.bedrooms ?? "Any",
        Array.isArray(params.price) ? params.price.join(", ") : params.price ?? "Any",
        searchTerm,
      );
    };

    const handleClearTracking = async (params: SearchParams, searchTerm: string | number) => {
      let location = "All location";
      if (params.category) {
        location = (await locationsFromID(params.category as string[])).join(",");
      }
      clearAllTrackingEvent(
        location,
        Array.isArray(params.propertyType) ? params.propertyType.join(", ") : params.propertyType ?? "Any",
        Array.isArray(params.bedrooms) ? params.bedrooms.join(", ") : params.bedrooms ?? "Any",
        Array.isArray(params.price) ? params.price.join(", ") : params.price ?? "Any",
        searchTerm,
      );
    };
    return (
      <AnimatePresence>
        {locations && (
          <motion.div
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -10 }}
            ref={containerRef}
            className={cn("filter-container w-full h-full flex items-center relative px-m", containerClassNames)}
          >
            <div className="w-full flex md:flex-col leading-none ">
              {icon && <Icon name={`${icon}`} size="24" className="stroke-black mr-s md:hidden" />}
              {!!label && (
                <Label
                  htmlFor={`autocomplete-text-${name}`}
                  className={`text-s font-secondary-light text-nowrap leading-none block filter-label ${isAutoGeneratedCarousel ? "text-black" : reverse || !isHomePage ? "text-white" : ""}`}
                  reverse={reverse}
                  required={rest?.required ?? false}
                >
                  {label}
                </Label>
              )}
              <div
                className={`flex ${locationValues.length > 0 ? "gap-xxs" : ""}`}
                onClick={() => {
                  innerInput?.current?.focus();
                }}
              >
                <span
                  className={`flex-grow text-nowrap border-none appearance-none h-m text-m block bg-transparent font-medium font-secondary leading-none mt-xxs filter-content ${reverse || !isHomePage ? "placeholder:text-white text-white" : "placeholder:text-black text-black"}`}
                >
                  {locationValues.length > 0
                    ? `${(() => {
                        // Get translation if available, otherwise use English name
                        const translation = language !== "en" ? getLocationTranslation(locationsId, language) : null;
                        const firstLocation = translation?.name || locationValues[0];

                        if (typeof firstLocation === "string") {
                          const words = firstLocation.split(" "); // Split the text into words
                          return words.length > 2
                            ? `${words.slice(0, 2).join(" ")}...` // Show first 2 words and add "..."
                            : firstLocation; // Show the full text if it's short
                        }
                        // Fallback to first location value if no valid translation
                        return locationValues[0] || "";
                      })()} ${t("search.label.locationList", { count: locationValues.length, first: locationValues[0] })}`
                    : ""}
                </span>
                <input
                  autoComplete="off"
                  ref={innerInput}
                  placeholder={locationValues.length === 0 ? rest?.placeholder : ""}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    setTextValue(e.currentTarget.value);
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onFocus={(e) => {
                    rest?.onFocus?.(e);
                    setDisplaySuggestions(true);
                  }}
                  onKeyDown={(e) => {
                    setTyping(true);
                    if (e.code === "Enter") {
                      // if (window?.innerWidth <= 1023) {
                      //   e.preventDefault();
                      //   e.stopPropagation();
                      // }

                      e.preventDefault();
                      e.stopPropagation();

                      // Find first active suggestion from filtered results
                      let firstActiveItem = null;
                      for (const locationType of Object.keys(filteredResults)) {
                        const activeItem = filteredResults[locationType].find((item) => item.active === true);
                        if (activeItem) {
                          firstActiveItem = activeItem;
                          break;
                        }
                      }

                      if (firstActiveItem && textValue.length >= minimumCharactersSuggestions) {
                        // Add to field value
                        const currentValues = Array.isArray(field?.value) ? field.value : [];
                        if (!currentValues.includes(firstActiveItem.id) && currentValues.length < MAX_AUTOCOMPLETE_SELECTION) {
                          const newValues = [...currentValues, firstActiveItem.id];
                          field?.onChange(newValues);
                          setValue({
                            action: "ADD",
                            value: firstActiveItem.id,
                          });
                          setLocationsId(firstActiveItem.id);

                          // Clear input and close suggestions
                          setTextValue("");
                          setDisplaySuggestions(false);

                          // Track selection
                          if (typeof setRecentSearch === "function") {
                            // setRecentSearch(firstActiveItem.id);
                            setUserCurrentSelection(firstActiveItem.id);
                          }
                        }
                      }
                    }
                    if (e.code === "ArrowDown") {
                    }
                  }}
                  onKeyUp={(e) => {
                    setTextValue(e.currentTarget.value);
                  }}
                  onBlur={(e) => {
                    rest?.onBlur?.(e);
                    setTyping(false);
                    // setDisplaySuggestions(false);
                  }}
                  type="text"
                  value={textValue}
                  id={`autocomplete-text-${name}`}
                  className={`focus:outline-none border-none appearance-none h-m text-m block bg-transparent font-medium font-secondary w-full leading-none mt-xxs filter-content ${isAutoGeneratedCarousel ? "placeholder:text-black text-black" : reverse || !isHomePage ? "placeholder:text-white text-white" : "placeholder:text-black text-black"}`}
                />
              </div>
            </div>
            <motion.div
              key={`autocomplete-list-${name}`}
              initial={"close"}
              variants={panelVariation}
              animate={displaySuggestions ? "open" : "close"}
              transition={{ duration: 0.3 }}
              /* md:drop-shadow md:backdrop-blur-xl */
              className={`autocomplete-list${reverse ? "-reverse" : ""} min-w-full w-max ${language === "ar" ? "" : "left-0"} absolute  md:p-l grid grid-flow-row gap-m before:rounded-[24px] rounded-[24px] ${isHomePage ? "top-3xl" : "bottom-full"} z-10`}
              style={{
                maxWidth: "347px",
                minWidth: "340px",
                ...(isHomePage ? {} : { bottom: "70px" }),
              }}
            >
              {/* User selection */}
              {field?.value?.length > 0 && (
                <div className="flex gap-xxs flex-wrap">
                  {field?.value.map((item: string) => {
                    const itemObject = locations?.find((location) => location.id === item);
                    return (
                      !!itemObject && (
                        <button
                          type="button"
                          onClick={() => {
                            let cleanValue = field?.value.filter((filterValue: string) => filterValue !== item);
                            field?.onChange(cleanValue);
                            setValue({
                              action: "REMOVE",
                              value: item,
                            });
                            innerInput?.current?.focus();
                            lastSearchClick(itemObject.name);
                          }}
                          className="bg-gold pr-s py-[6px] pl-[16px] p rounded-full text-s font-secondary"
                          key={`selected-${item}`}
                        >
                          {(() => {
                            const translatedData = language === "en" ? itemObject : getLocationTranslation(itemObject.id, language);
                            return (
                              <span className="flex flex-nowrap items-center">
                                {typeof translatedData?.name === "string" ? translatedData.name : itemObject?.name || ""}
                                <Icon name="close" size="16" className="stroke-black ml-s" />
                              </span>
                            );
                          })()}
                        </button>
                      )
                    );
                  })}
                </div>
              )}
              {/* Recent */}
              {recentItems.length > 0 && displayTrendingAndRecent ? (
                <div className={`${recentItems.length > 0 && displayTrendingAndRecent ? "" : "hidden"}`}>
                  <h3 className={`font-primary uppercase text-s overflow-hidden text-ellipsis`}>{t("search.label.latest")}</h3>
                  <div className={`autocomplete-content overflow-auto grid grid-flow-row gap-xs max-h-[160px]`}>
                    {recentItems.slice(0, 5).map((item) => {
                      const recentID = `recent-${item.id}`;
                      const disabled = field?.value?.includes(item.id) || field?.value?.length === MAX_AUTOCOMPLETE_SELECTION || false;
                      const translatedData = language === "en" ? item : getLocationTranslation(item.id, language);
                      return (
                        <label
                          className={`${disabled ? "opacity-40" : "cursor-pointer"} flex border-b border-b-gray-10 py-xs font-secondary text-m text-left ${reverse && !isHomePage ? "text-white" : "text-black"}`}
                          key={recentID}
                        >
                          <input
                            name={name}
                            {...field}
                            onChange={handleChange}
                            className="appearance-none hidden"
                            checked={field?.value ? field.value.includes(item.id) : false}
                            disabled={disabled}
                            type="checkbox"
                            value={item.id}
                          />
                          <span className="flex items-center gap-xs">
                            <Icon name="clock" size="16" className={`${reverse ? "stroke-white" : "stroke-black"}`} />
                            {/* {item.name}
                             */}
                            {typeof translatedData?.name === "string" ? translatedData.name : item?.name}
                            {item.type === "Project" && ` (${translatedData?.area || item.area})`}
                            {["Area", "Location"].includes(item.type) && ` (${translatedData?.city || item.city})`}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              ) : (
                // {/* Trending */}
                <div className={`${trendingItems.length > 0 && displayTrendingAndRecent ? "" : "hidden"}`}>
                  <h3
                    className={`font-primary uppercase text-s overflow-hidden text-ellipsis ${language === "ru" ? "leading-[140%]" : "leading-[120%]"}`}
                  >
                    {t("search.label.trending")}
                  </h3>
                  <div className={`autocomplete-content overflow-auto grid grid-flow-row gap-xs max-h-[170px]`}>
                    {trendingItems.slice(0, 5).map((item) => {
                      const trendingID = `trending-${item.id}`;
                      const disabled = field?.value?.includes(item.id) || field?.value?.length === MAX_AUTOCOMPLETE_SELECTION || false;

                      const translatedData = language === "en" ? item : getLocationTranslation(item.id, language);

                      return (
                        <label
                          className={`${disabled ? "opacity-40" : "cursor-pointer"} focus:outline-dotted flex border-b border-b-gray-10 py-xs font-secondary text-m text-left ${reverse ? "text-white" : "text-black"}`}
                          key={trendingID}
                        >
                          <input
                            name={name}
                            {...field}
                            onChange={handleChange}
                            checked={field?.value ? field.value.includes(item.id) : false}
                            className="appearance-none hidden"
                            disabled={disabled}
                            type="checkbox"
                            value={item.id}
                            onClick={(event) => {
                              event.stopPropagation();
                              if (!disabled) {
                                searchSuggestionClick(item.name);
                                setLocationsId(item.id);
                              }
                            }}
                          />
                          <span className="flex items-center gap-xs">
                            <Icon name="geotag" size="16" className={`${reverse ? "stroke-white" : "stroke-black"}`} />
                            {typeof translatedData?.name === "string" ? translatedData.name : item?.name}
                            {item.type === "Project" &&
                              ` (${(translatedData?.location || item?.location) ?? (translatedData?.area || item?.area) ?? ""})`}
                            {["Area", "Location"].includes(item.type) && ` (${translatedData?.city} ?? ${item?.city}) `}

                            {/* {["Area", "Location"].includes(item.type) && ` (${item.city}) `} */}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}
              <div
                className={`autocomplete-content w-fit overflow-auto grid grid-flow-row gap-m ml-[10px] ${fullHeight ? "max-h-[200px]" : "max-h-[200px]"} ${!!textValue && textValue.length >= minimumCharactersSuggestions ? "" : "hidden"}`}
              >
                {Object.keys(filteredResults).map((locationType: string) => {
                  return (
                    filteredResults[locationType].some((item) => item.active === true) && (
                      <div key={`location-group-${locationType}`} className="">
                        <h3 className="font-primary uppercase text-m overflow-hidden text-ellipsis">{t(`search.label.${locationType}`)}</h3>
                        <div className="flex flex-col mt-xs">
                          {filteredResults[locationType].map((item) => {
                            const suggestionID = `suggestion-${item.id}`;
                            const disabled = field?.value?.includes(item.id) || field?.value?.length === MAX_AUTOCOMPLETE_SELECTION || false;
                            const translatedData = language === "en" ? item : getLocationTranslation(item.id, language);

                            return (
                              <label
                                className={`border-b border-b-gray-10 py-xs flex font-secondary text-[14px] ${language !== "ar" ? "text-left" : "text-right"} ${reverse ? "text-white" : "text-black"} ${item.active ? "cursor-pointer" : "hidden"}`}
                                key={suggestionID}
                              >
                                <input
                                  name={name}
                                  {...field}
                                  onChange={(e) => {
                                    handleChange(e);
                                    setLocationsId(item.id);
                                  }}
                                  checked={field?.value ? field.value.includes(item.id) : false}
                                  className="mr-s"
                                  type="checkbox"
                                  value={item.id}
                                  disabled={disabled}
                                />
                                <div className="text-wrap">
                                  <span
                                    dangerouslySetInnerHTML={{
                                      __html: highlightInput(typeof translatedData?.name === "string" ? translatedData.name : item.name, textValue),
                                    }}
                                  />
                                  {item.type === "Project" &&
                                    ` (${(translatedData?.location || item?.location) ?? (translatedData?.area || item?.area) ?? ""})`}
                                  {["Area", "Location"].includes(item.type) && ` (${translatedData?.city || item.city})`}
                                </div>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    )
                  );
                })}
              </div>
              {field?.value && field.value.length === MAX_AUTOCOMPLETE_SELECTION && (
                <p className="font-primary text-s overflow-hidden text-ellipsis">{t(`search.label.maxLocationReached`)}</p>
              )}
              {/* Previous this condition was using "container === SEARCH_PAGE &&" */}
              {SEARCH_BAR_AS_PER_PAGE.includes(container) && (
                <FilterAction
                  resetHandler={() => {
                    setValue({
                      action: "CLEAR",
                      value: "",
                    });
                    setTextValue("");
                    setLocationsId("");
                    field?.onChange([]);
                  }}
                  // filterName="categories"
                  setDisplayFilters={setDisplaySuggestions as Dispatch<React.SetStateAction<string | boolean>>}
                  submitLabelDatalayer={() => {
                    handleFilterSelection(counterParams, t("button.label.done"));
                  }}
                  resetSearchFilters={() => {
                    handleClearTracking(counterParams, t("button.label.clearAll"));
                  }}
                />
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    );
  },
);
AutocompleteBase.displayName = "AutocompleteBase";
export default AutocompleteBase;
